import { PROTECTED_API_ENDPOINTS } from "../config/api-routes";
import { minutesToSeconds, secondsToMilliseconds } from "../helpers/date";
import { baseUrl } from "../helpers/http";
import logger from "../helpers/logger";
import { ROUTES } from "../routes";
import pages from "../routes";
import StorageService from "./storage-service";

const ACCESS_TOKEN = process.env.ACCESS_TOKEN_NAME ?? "accessToken";

export default class AuthService {
  static deleteAccessToken() {
    StorageService.removeItem(ACCESS_TOKEN);
  }

  static getAccessTokenKey() {
    return ACCESS_TOKEN;
  }

  static getBearerToken() { 
    const accessToken = AuthService.#getAccessToken();

    return `Bearer ${accessToken}`;
  }

  static saveAccessToken(authorization: { token: string; expiresIn: string; }) {
    const { token, expiresIn } = authorization;
    const expiresInSeconds = minutesToSeconds(parseInt(expiresIn, 10));

    AuthService.#saveAccessToken(token.replace("Bearer ", ""), expiresInSeconds);
  }

  static isAuthenticatedUser() { 
    const accessToken = AuthService.#getAccessToken();
  
    return accessToken !== null;
  }

  static isProtectedRoute(endpoint) {
    //const page = pages.find(page => page.route === endpoint); // frontend route.
    const page = pages.find(page => page.path === endpoint); // frontend route.

    return page?.authRequired || PROTECTED_API_ENDPOINTS.includes(endpoint);
  }

  // We placed this in AuthService because its redirecting is not just a
  // regular redirect to this url. Instead, what it does is,
  // when a user tries to access a protected page,
  // it redirects them to the login page. The login page then checks
  // if the user is authenticated. If they are, they are automatically
  // granted access to the protected resource. Otherwise, they are presented
  // with the login form.
  static redirect(path) {
    if(path === ROUTES.SIGNIN) {
      window.location = ROUTES.SIGNIN as string & Location;
      return;
    }

    if(AuthService.isAuthenticatedUser()) {
      window.location = encodeURIComponent(path) as string & Location;
      return;
    }

    window.location = `${ROUTES.SIGNIN}?redirect=${encodeURIComponent(path)}` as string & Location;
  }

  static async refreshAccessToken() {
    try {
      // Hit the refresh endpoint to fetch a new access token
      const refreshResponse = await fetch(`${baseUrl}/auth/token/refresh`, {
        method: "POST",
        credentials: "include", 
        headers: {
          "Content-Type": "application/json",
        },
      });

      if(refreshResponse.ok) {
        const json = await refreshResponse.json();
        const { authorization } = json.data;

        AuthService.deleteAccessToken();            // Delete the previous token
        AuthService.saveAccessToken(authorization); // Save the new token
        return true;
      } else {
        AuthService.deleteAccessToken();
        return false;
      } 
    } catch(error) { 
      logger.error("Error refreshing access token: ", error);
      AuthService.deleteAccessToken();
      throw error;
    }
  }

  static #getAccessToken() {
    const { token, expiry } = StorageService.getItem(ACCESS_TOKEN) ?? {};

    if(expiry && (expiry < Date.now())) {
      AuthService.deleteAccessToken(); // Delete the token from the file-system.

      return null;
    }

    return token ?? null;
  }

  /*static #isTokenValid(token) {
    try {
      // Decode JWT payload locally (no API call)
      const payload = JSON.parse(atob(token.split(".")[1]));

      // Check if token expiration time (exp) is in the future
      const isExpired = secondsToMilliseconds(payload.exp) < Date.now();

      return !(isExpired);
    } catch(error) {
      return false;
    }
  }*/

  /**
   * @param {String} token: The access token
   * @param {Number} ttl: The time (in seconds) when the token should expire.
   */
  static #saveAccessToken(token: string, ttl: number) {
    const expiry = ttl; //parseInt(ttl as unknown as string, 10);

    const data = { token, expiry: Date.now() + secondsToMilliseconds(expiry) };

    StorageService.setItem(ACCESS_TOKEN, data);
  }
}
